import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationApi, Notification } from '../services/api';
import { getPropertyUrl } from '../utils/formatters';
import './NotificationBell.css';

interface NotificationBellProps {
  onClose?: () => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 알림 목록 로드
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationApi.getList();
      setNotifications(response.notifications);
      setUnreadCount(response.unread_count);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 알림 로드
  useEffect(() => {
    loadNotifications();

    // 5분마다 알림 갱신
    const interval = setInterval(loadNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 벨 클릭 핸들러
  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      loadNotifications(); // 열 때 새로 로드
    }
  };

  // 알림 클릭 핸들러
  const handleNotificationClick = (notification: Notification) => {
    setIsOpen(false);
    // house_manage_no가 있으면 우선 사용, 없으면 property_id 사용
    navigate(getPropertyUrl({ id: notification.property_id, house_manage_no: notification.house_manage_no }));
    if (onClose) onClose();
  };

  // 날짜 포맷팅
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return '오늘';
    if (diffDays === 1) return '어제';
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  // 남은 기간 계산
  const getDaysLeft = (validUntil: string | null): string | null => {
    if (!validUntil) return null;
    const end = new Date(validUntil);
    const now = new Date();
    const diffMs = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return null;
    if (diffDays <= 3) return `D-${diffDays}`;
    if (diffDays <= 7) return `${diffDays}일 남음`;
    return null;
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className="notification-bell-btn"
        onClick={handleBellClick}
        aria-label="알림"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>알림</h3>
            {unreadCount > 0 && (
              <span className="notification-count">{unreadCount}개의 혜택</span>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">로딩 중...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <div className="empty-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <p>새로운 알림이 없습니다</p>
                <span className="empty-hint">
                  찜하거나 본 현장에 할인혜택이 생기면 알려드립니다
                </span>
              </div>
            ) : (
              notifications.map((notification) => {
                const daysLeft = getDaysLeft(notification.valid_until);
                return (
                  <div
                    key={notification.id}
                    className="notification-item"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-image">
                      {notification.property_image ? (
                        <img
                          src={notification.property_image}
                          alt={notification.property_title}
                        />
                      ) : (
                        <div className="no-image">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M20.4 14.5L16 10 4 20" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title-row">
                        <span className="notification-property-title">
                          {notification.property_title}
                        </span>
                        <span className={`notification-source ${notification.source}`}>
                          {notification.source === 'favorite' ? '찜' : '최근'}
                        </span>
                      </div>
                      <div className="notification-benefit">
                        <span className="benefit-tag">{notification.discount_text}</span>
                        <span className="benefit-title">{notification.benefit_title}</span>
                      </div>
                      <div className="notification-meta">
                        {daysLeft && (
                          <span className="days-left">{daysLeft}</span>
                        )}
                        <span className="notification-date">
                          {formatDate(notification.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <button
                className="view-all-btn"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/profile');
                }}
              >
                찜한 매물 보기
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
